import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { getLeaguePrintAllData, type PrintAllPayload } from "@/lib/print.functions";
import { renderAllBracketsPdf } from "@/lib/print-pdf.functions";
import { openPdfFromBase64 } from "@/lib/open-pdf";
import { getAccessibleLeagueBySlug } from "@/lib/access.functions";
import { hydrateMatchesForPrint, PrintablePage } from "@/components/PrintableBracket";
import { useAuth } from "@/lib/auth-context";
import "@/styles/print.css";

export const Route = createFileRoute("/print/$league/all")({
  component: PrintAllPage,
  head: () => ({
    meta: [
      { title: "Print All Brackets — Bracket Goblin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function PrintAllPage() {
  const { league: leagueSlug } = Route.useParams();
  const { session, loading: authLoading } = useAuth();
  const fn = useAuthedServerFn(getLeaguePrintAllData);
  const lookupLeague = useAuthedServerFn(getAccessibleLeagueBySlug);
  const renderPdfFn = useAuthedServerFn(renderAllBracketsPdf);
  const [data, setData] = useState<PrintAllPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleDownloadPdf = async () => {
    if (!data) return;
    setPdfLoading(true);
    setPdfError(null);
    try {
      const result = await renderPdfFn({ data: { leagueId: data.league.id } });
      openPdfFromBase64(result.pdfBase64, result.filename);
    } catch (e) {
      setPdfError(e instanceof Error ? e.message : "Failed to render PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading || !session) return;
    let cancelled = false;
    (async () => {
      try {
        const lg = await lookupLeague({ data: { slug: leagueSlug } });
        if (cancelled) return;
        const payload = await fn({ data: { leagueId: lg.id } });
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, session, leagueSlug, fn, lookupLeague]);

  const pages = useMemo(() => {
    if (!data) return [];
    return data.members.map((m) => ({
      member: m,
      matches: hydrateMatchesForPrint(m.picks, data.teams, data.r32Seeding),
    }));
  }, [data]);

  if (authLoading) return null;
  if (!session) {
    return (
      <div className="print-helper">
        <span>You need to sign in to view this page.</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="print-helper" style={{ color: "#b91c1c" }}>
        <span>{error}</span>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="print-helper">
        <span>Loading brackets…</span>
      </div>
    );
  }

  if (data.members.length === 0) {
    return (
      <div className="print-helper">
        <span>No approved members in this league yet.</span>
      </div>
    );
  }

  return (
    <div className="print-root">
      <div className="print-helper">
        <span>
          {pdfError ? (
            <span style={{ color: "#b91c1c" }}>{pdfError}</span>
          ) : (
            <>
              {data.members.length} brackets · one-click PDF or use{" "}
              <kbd>Ctrl</kbd>/<kbd>⌘</kbd> + <kbd>P</kbd>.
            </>
          )}
        </span>
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            style={{
              background: "#111",
              color: "#fff",
              border: "none",
              padding: "6px 12px",
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
              cursor: pdfLoading ? "wait" : "pointer",
              opacity: pdfLoading ? 0.6 : 1,
            }}
          >
            {pdfLoading ? "Generating PDF…" : "Download PDF"}
          </button>
          <span>{data.league.name}</span>
        </span>
      </div>
      {pages.map(({ member, matches }) => (
        <PrintablePage
          key={member.entryId}
          leagueName={data.league.name}
          memberDisplayName={member.displayName}
          memberRealName={member.realName}
          lockAtIso={data.league.bracketLockAt}
          matches={matches}
          championTotalKnockoutGoals={member.championTotalKnockoutGoals}
          bonusPicks={member.bonusPicks}
          bonusQuestions={data.bonusQuestions.map((q) => ({
            id: q.id,
            question_label: q.question_label,
            short_label: q.short_label,
            threshold: q.threshold,
            over_label: q.over_label,
            under_label: q.under_label,
          }))}
          groups={data.groups}
          groupPicks={member.groupPicks}
        />
      ))}
    </div>
  );
}
